// src/pages/Head-Of-Operations/ProjectDashboardModal.js
import React, { useState, useEffect, useCallback } from "react";
import { X, Briefcase, ListChecks, FileText, AlertTriangle, CheckSquare, DollarSign, ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import ProjectDetailsPanel from "../../components/head-of-Operations/ProjectDetailsPannel";
import MilestoneListPanel from "../../components/head-of-Operations/MilestoneListPanel";
import RequirementsPanel from "../../components/head-of-Operations/RequirementsPanel";
import RiskManagementPanel from "./RiskManagementPanel";
import ComplianceChecklistPanel from "../../components/head-of-Operations/ComplianceChecklistPanel";
import { fetchProjectById } from "../../api/projectApi";
import { getMilestonesByProject } from "../../api/milestoneApi";
import { getRequirementsByProjectId } from "../../api/requirementApi";
import { getRisks } from "../../api/riskApi"; 
import BudgetProcurementPanel from "../../components/head-of-Operations/BudgetProcurementPanel";
import { ComplianceProvider } from "./ComplianceContext";

const ProjectDashboardModal = ({ isOpen, onClose, projectId }) => {
    const [project, setProject] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [requirements, setRequirements] = useState([]);
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activePanel, setActivePanel] = useState("details"); 

    const fetchAllProjectData = useCallback(async () => {
        if (!projectId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const [
                projectData,
                milestonesData,
                requirementsData,
                risksDataResponse,
            ] = await Promise.all([
                fetchProjectById(projectId),
                getMilestonesByProject(projectId),
                getRequirementsByProjectId(projectId),
                getRisks(), 
            ]);

            const projectRisks = (risksDataResponse?.data || []).filter(risk => risk.project === projectId);

            if (!projectData) {
                throw new Error("Project not found.");
            }

            setProject(projectData);
            setMilestones(milestonesData || []);
            setRequirements(requirementsData || []); 
            setRisks(projectRisks);
            
        } catch (err) {
            console.error("Failed to fetch project data:", err);
            setError("Failed to fetch project data. Please try again.");
            toast.error("Failed to load project data.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (isOpen) {
            fetchAllProjectData();
        }
    }, [isOpen, fetchAllProjectData]);

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    };

    const panels = {
        details: {
            title: "Details",
            icon: <Briefcase size={18} />,
            component: <ProjectDetailsPanel project={project} />,
        },
        milestones: {
            title: "Milestones",
            icon: <ListChecks size={18} />,
            component: <MilestoneListPanel milestones={milestones} onUpdate={fetchAllProjectData} projectId={project?._id} />,
        },
        requirements: {
            title: "Requirements",
            icon: <FileText size={18} />,
            component: <RequirementsPanel projectId={project?._id} requirements={requirements} onUpdate={fetchAllProjectData} />,
        },
        risks: {
            title: "Risks",
            icon: <AlertTriangle size={18} />,
            component: <RiskManagementPanel projectId={project?._id} risks={risks} onUpdate={fetchAllProjectData} />,
        },
        compliance: {
            title: "Compliance",
            icon: <CheckSquare size={18} />,
            component: (
                <ComplianceProvider projectId={projectId} project={project}>
                    <ComplianceChecklistPanel projectId={projectId} onUpdate={fetchAllProjectData} setActivePanel={setActivePanel} />
                </ComplianceProvider>
            ),
        },
        budget: {
            title: "Budget & Procurement",
            icon: <DollarSign size={18} />,
            component: <BudgetProcurementPanel project={project} onUpdate={fetchAllProjectData} />,
        },
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <motion.div
                className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-7xl h-[90vh] flex flex-col"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-3xl font-bold text-orange-600">Project Dashboard: {project?.projectTitle}</h2>
                    <button type="button" onClick={onClose} className="text-4xl text-gray-400 hover:text-red-500 font-light transition-colors">&times;</button>
                </div>

                <div className="flex justify-center items-center mb-6">
                    <div className="flex bg-gray-200 rounded-lg p-1 flex-wrap">
                        {Object.keys(panels).map((key) => (
                             <button
                                key={key}
                                onClick={() => setActivePanel(key)}
                                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${activePanel === key ? "bg-orange-600 text-white shadow" : "text-gray-700 hover:bg-gray-300"}`}
                            >
                                {panels[key].icon}
                                <span className="ml-2">{panels[key].title}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                        <svg className="animate-spin h-8 w-8 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-3 text-lg font-semibold">Loading project data...</p>
                    </div>
                ) : error ? (
                    <div className="flex-1 flex items-center justify-center text-red-500 font-semibold text-lg">
                        {error}
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto">
                        {panels[activePanel].component}
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default ProjectDashboardModal;